import { Box, Card, CardContent, ToggleButton, ToggleButtonGroup, Typography, Divider } from '@mui/material';
import { FormHandles } from '@unform/core';
import { Form } from '../../shared/forms';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { VTextField } from '../../shared/forms';
import { Button } from '../../shared/components/MUI/button/Button';
import { authService } from '../../shared/services';

const forgotSchema = Yup.object().shape({
  email: Yup.string().email('E-mail inválido'),
  phone: Yup.string().test('phone-ddd', 'Telefone deve conter DDD (ex: 51999999999)', (val) => {
    if (!val) return true;
    const digits = val.replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 11;
  }),
});

export const ForgotPassword: React.FC = () => {
  const [sent, setSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [usePhone, setUsePhone] = useState(false);
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();

  const handleSubmit = useCallback(async (data: { email?: string; phone?: string }) => {
    try {
      setErrorMessage(undefined);
      formRef.current?.setErrors({});

      if (!data.email && !data.phone) {
        formRef.current?.setErrors({
          email: 'Informe o e-mail ou telefone',
          phone: 'Informe o e-mail ou telefone',
        });
        return;
      }

      await forgotSchema.validate(data, { abortEarly: false });

      setLoading(true);
      await authService.forgotPassword({
        email: data.email || undefined,
        phone: data.phone ? data.phone.replace(/\D/g, '') : undefined,
      });
      setSent(true);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors: Record<string, string> = {};
        err.inner.forEach((e) => {
          if (e.path) errors[e.path] = e.message;
        });
        formRef.current?.setErrors(errors);
      } else {
        setErrorMessage('Erro ao enviar solicitação. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleClickSend = useCallback(() => {
    formRef.current?.submitForm();
  }, []);

  return (
    <Box
      width='100vw'
      height='100vh'
      display='flex'
      alignItems='center'
      justifyContent='center'
    >
      <Card>
        <Box padding={2} width={400}>
          <CardContent>
            <Typography variant='h5' align='center'>
              Recuperar senha
            </Typography>

            <Divider variant='middle' sx={{ my: 2 }} />

            {sent ? (
              <Box textAlign='center'>
                <Typography variant='body1' sx={{ mb: 2 }}>
                  Se os dados informados existirem, você receberá um link de recuperação.
                </Typography>
                <Button
                  label='Voltar ao login'
                  variant='contained'
                  onClick={() => navigate('/')}
                />
              </Box>
            ) : (
              <Form ref={formRef} onSubmit={handleSubmit}>
                <Box display='flex' flexDirection='column' gap={2}>
                  {errorMessage && (
                    <Typography color='error' variant='body2'>
                      {errorMessage}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <ToggleButtonGroup
                      value={usePhone ? 'phone' : 'email'}
                      exclusive
                      size='small'
                      onChange={(_, value) => {
                        if (value) {
                          setUsePhone(value === 'phone');
                          formRef.current?.setErrors({});
                          formRef.current?.setFieldValue('email', '');
                          formRef.current?.setFieldValue('phone', '');
                        }
                      }}
                      fullWidth
                    >
                      <ToggleButton value='email' sx={{ flex: 1 }}>E-mail</ToggleButton>
                      <ToggleButton value='phone' sx={{ flex: 1 }}>Telefone</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {usePhone ? (
                    <VTextField
                      fullWidth
                      label='Telefone (com DDD)'
                      name='phone'
                      disabled={loading}
                      placeholder='Ex: 51999999999'
                    />
                  ) : (
                    <VTextField
                      fullWidth
                      label='E-mail'
                      name='email'
                      type='email'
                      disabled={loading}
                    />
                  )}

                  <Button
                    label='Enviar link de recuperação'
                    variant='contained'
                    disabled={loading}
                    isLoading={loading}
                    onClick={handleClickSend}
                  />

                  <Button
                    label='Voltar ao login'
                    variant='text'
                    onClick={() => navigate('/')}
                    disabled={loading}
                  />
                </Box>
              </Form>
            )}
          </CardContent>
        </Box>
      </Card>
    </Box>
  );
};
